import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  CloudRain, Edit, Plus, Printer, Trash2, X, Upload, MapPin, AlertTriangle, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const NWS_LEVELS = ['None', 'Advisory', 'Watch', 'Warning', 'Emergency'];
const NWS_STYLE = {
  None:       { bg: '#374151', fg: '#F9FAFB', border: '#4B5563' },
  Advisory:   { bg: '#166534', fg: '#DCFCE7', border: '#22C55E' },
  Watch:      { bg: '#854D0E', fg: '#FEF3C7', border: '#EAB308' },
  Warning:    { bg: '#9A3412', fg: '#FFEDD5', border: '#F97316' },
  Emergency:  { bg: '#7F1D1D', fg: '#FEE2E2', border: '#EF4444' },
};

const SKY_COVER = [
  'Observe the whole sky for coverage',
  'SKC - Sky Clear',
  'FEW - Few clouds (1-2 oktas)',
  'SCT - Scattered (3-4 oktas)',
  'BKN - Broken (5-7 oktas)',
  'OVC - Overcast (8 oktas)',
];

const CARDINAL = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const TS_QUADRANTS = ['N','NE','E','SE','S','SW','W','NW'];

const INTENSITY = ['', 'Light', 'Moderate', 'Heavy'];

// ---------- Validators ----------
const TIME_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const DEC4_RE = /^-?\d+(\.\d{1,4})?$/;
const validateTime = (v) => {
  if (!v) return 'Time is required';
  return TIME_RE.test(v) ? '' : 'Use 24-hour HH:MM (e.g. 17:30)';
};
const validateLat = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (!DEC4_RE.test(String(v))) return 'Decimal to 4 digits (e.g. 47.0000)';
  const n = parseFloat(v);
  if (Number.isNaN(n) || n < -90 || n > 90) return 'LAT must be between -90.0000 and 90.0000';
  return '';
};
const validateLon = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  if (!DEC4_RE.test(String(v))) return 'Decimal to 4 digits (e.g. -97.3456)';
  const n = parseFloat(v);
  if (Number.isNaN(n) || n < -180 || n > 180) return 'LON must be between -180.0000 and 180.0000';
  return '';
};

// ---------- Empty form template ----------
const emptyConditions = () => ({
  moist_obscuration: { fog: false, mist: false, freezing_fog: false, shallow_fog: false },
  dry_obscuration: { haze: false, dust: false, smoke: false, ash: false },
  liquid_precipitation: {
    rain: false, rain_intensity: '',
    rain_shower: false, rain_shower_intensity: '',
    freezing_rain: false, mixed_precipitation: false, drizzle: false,
  },
  frozen_precipitation: {
    snow: false, snow_intensity: '',
    blowing_snow: false, ice_pellets: false, ice_crystals: false, snow_grains: false,
  },
  intense_phenomena: {
    thunderstorm: false, ts_in_vicinity: false, ts_quadrant: '',
    hail: false, sand_duststorm: false, funnel_cloud: false,
  },
});

const emptyForm = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    agency_name: '',
    is_exercise: true,
    call_sign: '',
    observer_name: '',
    location: '',
    city: '',
    state: '',
    county: '',
    latitude: '',
    longitude: '',
    mgrs: '',
    grid: '',
    units: 'metric',
    sky_cover: SKY_COVER[0],
    current_conditions: emptyConditions(),
    temperature: '',
    humidity: '',
    dewPoint: '',
    barometer: '',
    three_hour_trend: 'steady',
    wind_direction_from: '',
    windSpeed: '',
    wind_gusts: '',
    wind_gusts_max: '',
    rain_1hr: '',
    rain_total: '',
    snow_1hr: '',
    snow_total: '',
    nws_level: 'None',
    nws_details: '',
    notes: '',
  };
};

// Merge server observation into form-shape (adds missing keys)
const toForm = (obs) => {
  const base = emptyForm();
  const cc = obs.current_conditions || {};
  // Coerce any null/undefined optional field to empty string to avoid React controlled-input warnings
  const cleaned = Object.fromEntries(
    Object.entries(obs || {}).map(([k, v]) => [k, v === null || v === undefined ? '' : v])
  );
  return {
    ...base,
    ...cleaned,
    current_conditions: {
      ...base.current_conditions,
      ...cc,
      moist_obscuration: { ...base.current_conditions.moist_obscuration, ...(cc.moist_obscuration) },
      dry_obscuration: { ...base.current_conditions.dry_obscuration, ...(cc.dry_obscuration) },
      liquid_precipitation: { ...base.current_conditions.liquid_precipitation, ...(cc.liquid_precipitation) },
      frozen_precipitation: { ...base.current_conditions.frozen_precipitation, ...(cc.frozen_precipitation) },
      intense_phenomena: { ...base.current_conditions.intense_phenomena, ...(cc.intense_phenomena) },
    },
    nws_level: obs.nws_level || 'None',
    is_exercise: obs.is_exercise !== false,
    units: obs.units || base.units,
  };
};

// Serialize form for backend (numbers as numbers)
const toPayload = (form) => {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    ...form,
    latitude: num(form.latitude),
    longitude: num(form.longitude),
    temperature: num(form.temperature),
    humidity: num(form.humidity),
    dewPoint: num(form.dewPoint),
    barometer: num(form.barometer),
    windSpeed: num(form.windSpeed),
    wind_gusts: num(form.wind_gusts),
    wind_gusts_max: num(form.wind_gusts_max),
    rain_1hr: num(form.rain_1hr),
    rain_total: num(form.rain_total),
    snow_1hr: num(form.snow_1hr),
    snow_total: num(form.snow_total),
  };
};

// ---------- Sub-components ----------
const Field = ({ label, children, className = '' }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-gray-300 text-xs uppercase tracking-wide">{label}</Label>
    {children}
  </div>
);

const CheckRow = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-gray-200 text-sm cursor-pointer select-none">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 accent-blue-500"
      data-testid={testId}
    />
    <span>{label}</span>
  </label>
);

const RiskPill = ({ level }) => {
  const s = NWS_STYLE[level] || NWS_STYLE.None;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
      data-testid={`risk-pill-${level}`}
    >
      {level !== 'None' && <AlertTriangle className="h-3 w-3" />}
      {level}
    </span>
  );
};

// ---------- Print HTML ----------
const buildPrintHTML = (obs) => {
  const cc = obs.current_conditions || {};
  const mo = cc.moist_obscuration || {};
  const doo = cc.dry_obscuration || {};
  const lp = cc.liquid_precipitation || {};
  const fp = cc.frozen_precipitation || {};
  const ip = cc.intense_phenomena || {};
  const yes = (v) => (v ? '☑' : '☐');
  const risk = NWS_STYLE[obs.nws_level] || NWS_STYLE.None;
  const units = obs.units === 'imperial' ? { t: '°F', s: 'mph', d: 'in' } : { t: '°C', s: 'km/h', d: 'mm' };
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>WX Report ${obs.date} ${obs.time}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color:#111; margin:20px; }
  h1 { text-align:center; background:#DBEAFE; padding:8px; margin:0 0 10px; font-size:18px; border:1px solid #93C5FD; }
  .banner { display:inline-block; padding:4px 10px; border-radius:12px; font-weight:700; }
  table { border-collapse: collapse; width:100%; margin-bottom:8px; }
  td, th { border:1px solid #9CA3AF; padding:5px 8px; font-size:12px; vertical-align:top; }
  th { background:#F3F4F6; text-align:left; }
  .cols { display:grid; grid-template-columns:repeat(5,1fr); gap:6px; margin:6px 0; font-size:12px; }
  .col h3 { font-size:12px; margin:0 0 4px; text-decoration:underline; }
  .col div { line-height:1.5; }
  .footer { margin-top:12px; font-size:10px; color:#555; text-align:right; }
  @media print { .no-print { display:none; } }
</style></head><body>
<h1>CURRENT LOCAL WEATHER CONDITIONS</h1>
<div style="text-align:center;margin-bottom:8px;">
  ${obs.is_exercise ? '<span class="banner" style="background:#DBEAFE;border:1px solid #60A5FA;">☑ THIS IS AN EXERCISE</span>' : ''}
  &nbsp;&nbsp;
  <span class="banner" style="background:${risk.bg};color:${risk.fg};border:1px solid ${risk.border};">Risk Level: ${obs.nws_level || 'None'}</span>
</div>
<table>
  <tr><th style="width:20%">1. Call sign</th><td>${obs.call_sign || ''}</td>
      <th style="width:20%">2. Observer Name</th><td>${obs.observer_name || ''}</td></tr>
  <tr><th>3. Report Date/Time</th><td>${obs.date || ''} ${obs.time || ''}</td>
      <th>4. Location</th><td>${obs.location || ''}</td></tr>
  <tr><th>5. City</th><td>${obs.city || ''}</td>
      <th>6. State / 7. County</th><td>${obs.state || ''} / ${obs.county || ''}</td></tr>
  <tr><th>8. LAT / 9. LON</th><td>${obs.latitude ?? ''} / ${obs.longitude ?? ''}</td>
      <th>10. MGRS / 11. Grid</th><td>${obs.mgrs || ''} / ${obs.grid || ''}</td></tr>
  <tr><th>12. Units</th><td>${obs.units || 'metric'}</td>
      <th>13. Sky Cover</th><td>${obs.sky_cover || ''}</td></tr>
</table>
<div><strong>14. Current Conditions</strong></div>
<div class="cols">
  <div class="col"><h3>Moist Obscuration</h3>
    <div>${yes(mo.fog)} FOG</div><div>${yes(mo.mist)} MIST</div>
    <div>${yes(mo.freezing_fog)} FREEZING FOG</div><div>${yes(mo.shallow_fog)} SHALLOW FOG</div>
  </div>
  <div class="col"><h3>Dry Obscuration</h3>
    <div>${yes(doo.haze)} HAZE</div><div>${yes(doo.dust)} DUST</div>
    <div>${yes(doo.smoke)} SMOKE</div><div>${yes(doo.ash)} ASH</div>
  </div>
  <div class="col"><h3>Liquid Precipitation</h3>
    <div>${yes(lp.rain)} RAIN ${lp.rain_intensity ? '('+lp.rain_intensity+')' : ''}</div>
    <div>${yes(lp.rain_shower)} RAIN SHOWER ${lp.rain_shower_intensity ? '('+lp.rain_shower_intensity+')' : ''}</div>
    <div>${yes(lp.freezing_rain)} FREEZING RAIN</div>
    <div>${yes(lp.mixed_precipitation)} MIXED PRECIP</div>
    <div>${yes(lp.drizzle)} DRIZZLE</div>
  </div>
  <div class="col"><h3>Frozen Precipitation</h3>
    <div>${yes(fp.snow)} SNOW ${fp.snow_intensity ? '('+fp.snow_intensity+')' : ''}</div>
    <div>${yes(fp.blowing_snow)} BLOWING SNOW</div>
    <div>${yes(fp.ice_pellets)} ICE PELLETS</div>
    <div>${yes(fp.ice_crystals)} ICE CRYSTALS</div>
    <div>${yes(fp.snow_grains)} SNOW GRAINS</div>
  </div>
  <div class="col"><h3>Intense Phenomena</h3>
    <div>${yes(ip.thunderstorm)} THUNDERSTORM</div>
    <div>${yes(ip.ts_in_vicinity)} TS IN VICINITY ${ip.ts_quadrant ? '('+ip.ts_quadrant+')' : ''}</div>
    <div>${yes(ip.hail)} HAIL</div>
    <div>${yes(ip.sand_duststorm)} SAND/DUSTSTORM</div>
    <div>${yes(ip.funnel_cloud)} FUNNEL/TORNADO</div>
  </div>
</div>
<table>
  <tr>
    <th>15. Temperature</th><td>${obs.temperature ?? ''} ${units.t}</td>
    <th>16. Humidity</th><td>${obs.humidity ?? ''} %</td>
    <th>17. Dew Point</th><td>${obs.dewPoint ?? ''} ${units.t}</td>
  </tr>
  <tr>
    <th>18. Barometer</th><td>${obs.barometer ?? ''} mb</td>
    <th>19. 3-hr Trend</th><td colspan="3">${obs.three_hour_trend || ''}</td>
  </tr>
  <tr>
    <th>20/21. Wind</th><td>${obs.windSpeed ?? ''} ${units.s} from ${obs.wind_direction_from || ''}</td>
    <th>22. Gusts</th><td>${obs.wind_gusts ?? ''} ${units.s}</td>
    <th>23. Gusts Max</th><td>${obs.wind_gusts_max ?? ''} ${units.s}</td>
  </tr>
  <tr>
    <th>24. Rain 1HR</th><td>${obs.rain_1hr ?? ''} ${units.d}</td>
    <th>25. Rain Total</th><td>${obs.rain_total ?? ''} ${units.d}</td>
    <th>26/27. Snow 1HR/Total</th><td>${obs.snow_1hr ?? ''} / ${obs.snow_total ?? ''} cm</td>
  </tr>
  <tr><th>28. NWS Level</th><td colspan="5">${obs.nws_level || 'None'}${obs.nws_details ? ' — ' + obs.nws_details : ''}</td></tr>
  <tr><th>29. Notes</th><td colspan="5" style="white-space:pre-wrap;">${(obs.notes || '').replace(/[<>&]/g, (c)=>({ '<':'&lt;','>':'&gt;','&':'&amp;' })[c])}</td></tr>
</table>
<div class="footer">EXRSIM Weather Report · Form Concept by KF5SMH · Ver 4.0.6 K7IBC</div>
<div class="no-print" style="margin-top:10px;text-align:center;"><button onclick="window.print()">Print</button></div>
</body></html>`;
};

// ---------- Main component ----------
const WXReportModule = ({ theme = 'dark', exerciseId }) => {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState({ time: '', latitude: '', longitude: '' });
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { if (exerciseId) load(); }, [exerciseId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/weather-observations?exercise_id=${exerciseId}`);
      setObservations(res.data || []);
    } catch (e) {
      console.error('Load WX reports failed:', e);
    } finally { setLoading(false); }
  };

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === 'time') setFieldErrors((e) => ({ ...e, time: validateTime(v) }));
    if (k === 'latitude') setFieldErrors((e) => ({ ...e, latitude: validateLat(v) }));
    if (k === 'longitude') setFieldErrors((e) => ({ ...e, longitude: validateLon(v) }));
  };
  const setCC = (col, key, v) => setForm((f) => ({
    ...f,
    current_conditions: { ...f.current_conditions, [col]: { ...f.current_conditions[col], [key]: v } }
  }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setFieldErrors({ time: '', latitude: '', longitude: '' }); setShowForm(true); };
  const openEdit = (obs) => { setEditing(obs); setForm(toForm(obs)); setFieldErrors({ time: '', latitude: '', longitude: '' }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditing(null); setFieldErrors({ time: '', latitude: '', longitude: '' }); };

  const save = async () => {
    if (!exerciseId) { alert('No active exercise'); return; }
    if (!form.date || !form.time || !form.location) {
      alert('Date, Time and Location are required'); return;
    }
    const errs = {
      time: validateTime(form.time),
      latitude: validateLat(form.latitude),
      longitude: validateLon(form.longitude),
    };
    setFieldErrors(errs);
    if (errs.time || errs.latitude || errs.longitude) {
      alert('Please fix highlighted fields:\n' + Object.values(errs).filter(Boolean).join('\n'));
      return;
    }
    try {
      const payload = { ...toPayload(form), exercise_id: exerciseId };
      if (editing?.id) {
        await axios.put(`${API}/weather-observations/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/weather-observations`, payload);
      }
      await load();
      closeForm();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to save report');
    }
  };

  const remove = async (id) => {
    if (!confirm('Delete this weather report?')) return;
    try {
      await axios.delete(`${API}/weather-observations/${id}`);
      await load();
    } catch (e) { alert('Delete failed'); }
  };

  const printOne = (obs) => {
    const w = window.open('', '_blank');
    if (!w) { alert('Popup blocked'); return; }
    writeSanitizedHTML(w.document, buildPrintHTML(obs));
    w.focus();
    setTimeout(() => { try { w.print(); } catch(e){} }, 400);
  };

  const useGPS = () => {
    if (!navigator.geolocation) { alert('Geolocation not available'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({
        ...f,
        latitude: pos.coords.latitude.toFixed(4),
        longitude: pos.coords.longitude.toFixed(4),
      })),
      (err) => alert('GPS error: ' + err.message)
    );
  };

  const onImportPick = () => fileRef.current?.click();
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!exerciseId) { alert('No active exercise'); return; }
    setImporting(true); setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('exercise_id', exerciseId);
      const res = await axios.post(`${API}/weather-observations/import-winlink`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportMsg(`Imported ${res.data.imported} row(s)${res.data.errors?.length ? ' (' + res.data.errors.length + ' skipped)' : ''}`);
      await load();
    } catch (err) {
      console.error(err);
      setImportMsg('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally { setImporting(false); }
  };

  const sorted = useMemo(
    () => [...observations].sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`)),
    [observations]
  );

  if (!exerciseId) {
    return (
      <div className="p-6 text-gray-300">
        <div className="text-lg font-semibold mb-1">WX Report</div>
        <div className="text-sm">Select an active exercise to view or add weather reports.</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="wx-report-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CloudRain className="h-6 w-6 text-blue-400" />
          <h2 className="text-xl md:text-2xl font-bold text-white">WX Report</h2>
          <span className="text-gray-400 text-sm">({observations.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={onImportPick}
            variant="outline"
            className="border-gray-600 text-gray-200 hover:bg-gray-700"
            data-testid="wx-import-winlink-btn"
            disabled={importing}
          >
            <Upload className="h-4 w-4 mr-1" />
            {importing ? 'Importing…' : 'Import Winlink CSV'}
          </Button>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wx-new-report-btn">
            <Plus className="h-4 w-4 mr-1" /> New Report
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onImportFile} className="hidden" data-testid="wx-import-file-input" />
        </div>
      </div>
      {importMsg && <div className="text-sm text-blue-300" data-testid="wx-import-msg">{importMsg}</div>}

      {/* Log view */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-2 text-xs font-semibold text-gray-400 uppercase bg-gray-900/60">
          <div className="col-span-3">Date / Time</div>
          <div className="col-span-3">Location</div>
          <div className="col-span-2">Observer</div>
          <div className="col-span-2">Risk Level</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="p-4 text-gray-400">Loading…</div>}
        {!loading && sorted.length === 0 && (
          <div className="p-6 text-gray-400 text-center" data-testid="wx-empty">
            No weather reports yet. Click <span className="text-blue-400">New Report</span> or <span className="text-blue-400">Import Winlink CSV</span>.
          </div>
        )}
        {sorted.map((obs) => (
          <div key={obs.id} className="grid grid-cols-12 items-center px-3 py-2 border-t border-gray-700 hover:bg-gray-700/40" data-testid={`wx-row-${obs.id}`}>
            <div className="col-span-3 text-gray-100 text-sm font-mono">{obs.date} · {obs.time}</div>
            <div className="col-span-3 text-gray-200 text-sm truncate">{obs.location || '—'}</div>
            <div className="col-span-2 text-gray-300 text-sm truncate">{obs.observer_name || obs.call_sign || '—'}</div>
            <div className="col-span-2"><RiskPill level={obs.nws_level || 'None'} /></div>
            <div className="col-span-2 flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => printOne(obs)} className="text-gray-300 hover:text-white" data-testid={`wx-print-${obs.id}`}>
                <Printer className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(obs)} className="text-gray-300 hover:text-white" data-testid={`wx-edit-${obs.id}`}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(obs.id)} className="text-red-400 hover:text-red-300" data-testid={`wx-delete-${obs.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" data-testid="wx-form-modal">
          <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-5xl my-6 shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-900 rounded-t-lg z-10">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">
                  {editing ? 'Edit Weather Report' : 'New Weather Report'}
                </h3>
              </div>
              <button onClick={closeForm} className="text-gray-400 hover:text-white" data-testid="wx-form-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-gray-100">
                  <input type="checkbox" checked={form.is_exercise} onChange={(e)=>setField('is_exercise', e.target.checked)} className="h-4 w-4 accent-blue-500" data-testid="wx-is-exercise" />
                  <span className="font-semibold">THIS IS AN EXERCISE</span>
                </label>
                <Input value={form.agency_name} onChange={(e)=>setField('agency_name', e.target.value)} placeholder="Agency / group name (optional)" className="bg-gray-800 border-gray-700 text-white flex-1" data-testid="wx-agency" />
              </div>

              {/* 1-7 identification */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="1. Call sign"><Input value={form.call_sign} onChange={(e)=>setField('call_sign', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-callsign" /></Field>
                <Field label="2. Observer Name"><Input value={form.observer_name} onChange={(e)=>setField('observer_name', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-observer" /></Field>
                <Field label="3a. Report Date">
                  <Input type="date" value={form.date} onChange={(e)=>setField('date', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-date" />
                </Field>
                <Field label="3b. Report Time (24h HH:MM)">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="^([01][0-9]|2[0-3]):[0-5][0-9]$"
                    maxLength={5}
                    placeholder="17:30"
                    value={form.time}
                    onChange={(e)=>setField('time', e.target.value)}
                    className={`bg-gray-800 border text-white ${fieldErrors.time ? 'border-red-500' : 'border-gray-700'}`}
                    data-testid="wx-time"
                  />
                  {fieldErrors.time && <div className="text-red-400 text-xs mt-1" data-testid="wx-time-error">{fieldErrors.time}</div>}
                </Field>
                <Field label="4. Location *" className="md:col-span-2">
                  <Input value={form.location} onChange={(e)=>setField('location', e.target.value)} placeholder="Street name or known closest landmark" className="bg-gray-800 border-gray-700 text-white" data-testid="wx-location" />
                </Field>
                <Field label="5. City"><Input value={form.city} onChange={(e)=>setField('city', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-city" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="6. State/Prov"><Input value={form.state} onChange={(e)=>setField('state', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-state" /></Field>
                  <Field label="7. County"><Input value={form.county} onChange={(e)=>setField('county', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-county" /></Field>
                </div>
              </div>

              {/* Coordinates */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-gray-300 text-sm font-semibold">Latitude and Longitude</div>
                  <Button size="sm" onClick={useGPS} variant="outline" className="border-gray-600 text-gray-200" data-testid="wx-gps-btn">
                    <MapPin className="h-3.5 w-3.5 mr-1" /> Use GPS
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Field label="8. LAT">
                    <Input
                      value={form.latitude}
                      onChange={(e)=>setField('latitude', e.target.value)}
                      placeholder="47.0000  (-90.0000 to 90.0000)"
                      className={`bg-gray-800 border text-white ${fieldErrors.latitude ? 'border-red-500' : 'border-gray-700'}`}
                      data-testid="wx-lat"
                    />
                    {fieldErrors.latitude && <div className="text-red-400 text-xs mt-1" data-testid="wx-lat-error">{fieldErrors.latitude}</div>}
                  </Field>
                  <Field label="9. LON">
                    <Input
                      value={form.longitude}
                      onChange={(e)=>setField('longitude', e.target.value)}
                      placeholder="-97.3456  (-180.0000 to 180.0000)"
                      className={`bg-gray-800 border text-white ${fieldErrors.longitude ? 'border-red-500' : 'border-gray-700'}`}
                      data-testid="wx-lon"
                    />
                    {fieldErrors.longitude && <div className="text-red-400 text-xs mt-1" data-testid="wx-lon-error">{fieldErrors.longitude}</div>}
                  </Field>
                  <Field label="10. MGRS"><Input value={form.mgrs} onChange={(e)=>setField('mgrs', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-mgrs" /></Field>
                  <Field label="11. Grid"><Input value={form.grid} onChange={(e)=>setField('grid', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-grid" /></Field>
                </div>
                <div className="text-xs text-red-400">Verify entered coordinates before submitting form.</div>
              </div>

              {/* Units & Sky */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="12. Default Units">
                  <div className="flex items-center gap-4 text-gray-100">
                    <label className="flex items-center gap-2"><input type="radio" name="units" checked={form.units==='imperial'} onChange={()=>setField('units','imperial')} data-testid="wx-units-imperial"/> Imperial</label>
                    <label className="flex items-center gap-2"><input type="radio" name="units" checked={form.units==='metric'} onChange={()=>setField('units','metric')} data-testid="wx-units-metric"/> Metric</label>
                  </div>
                </Field>
                <Field label="13. Sky Cover" className="md:col-span-2">
                  <select value={form.sky_cover} onChange={(e)=>setField('sky_cover', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="wx-sky-cover">
                    {SKY_COVER.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              {/* 14. Current Conditions grid */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40">
                <div className="text-gray-200 font-semibold mb-2">14. Current Conditions <span className="text-gray-400 font-normal text-xs">(check all that apply)</span></div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Moist */}
                  <div>
                    <div className="text-blue-300 font-semibold text-sm underline mb-1">Moist Obscuration</div>
                    <CheckRow label="FOG" checked={form.current_conditions.moist_obscuration.fog} onChange={(v)=>setCC('moist_obscuration','fog',v)} testId="wx-cc-fog" />
                    <CheckRow label="MIST" checked={form.current_conditions.moist_obscuration.mist} onChange={(v)=>setCC('moist_obscuration','mist',v)} testId="wx-cc-mist" />
                    <CheckRow label="FREEZING FOG" checked={form.current_conditions.moist_obscuration.freezing_fog} onChange={(v)=>setCC('moist_obscuration','freezing_fog',v)} testId="wx-cc-freezing-fog" />
                    <CheckRow label="SHALLOW FOG" checked={form.current_conditions.moist_obscuration.shallow_fog} onChange={(v)=>setCC('moist_obscuration','shallow_fog',v)} testId="wx-cc-shallow-fog" />
                  </div>
                  {/* Dry */}
                  <div>
                    <div className="text-blue-300 font-semibold text-sm underline mb-1">Dry Obscuration</div>
                    <CheckRow label="HAZE" checked={form.current_conditions.dry_obscuration.haze} onChange={(v)=>setCC('dry_obscuration','haze',v)} testId="wx-cc-haze" />
                    <CheckRow label="DUST" checked={form.current_conditions.dry_obscuration.dust} onChange={(v)=>setCC('dry_obscuration','dust',v)} testId="wx-cc-dust" />
                    <CheckRow label="SMOKE" checked={form.current_conditions.dry_obscuration.smoke} onChange={(v)=>setCC('dry_obscuration','smoke',v)} testId="wx-cc-smoke" />
                    <CheckRow label="ASH" checked={form.current_conditions.dry_obscuration.ash} onChange={(v)=>setCC('dry_obscuration','ash',v)} testId="wx-cc-ash" />
                  </div>
                  {/* Liquid */}
                  <div>
                    <div className="text-blue-300 font-semibold text-sm underline mb-1">Liquid Precipitation</div>
                    <div className="flex items-center gap-2">
                      <CheckRow label="RAIN (RA)" checked={form.current_conditions.liquid_precipitation.rain} onChange={(v)=>setCC('liquid_precipitation','rain',v)} testId="wx-cc-rain" />
                      <select value={form.current_conditions.liquid_precipitation.rain_intensity} onChange={(e)=>setCC('liquid_precipitation','rain_intensity',e.target.value)} className="bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-1 py-0.5">
                        {INTENSITY.map((i) => <option key={i} value={i}>{i || 'Intensity'}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckRow label="RAIN SHOWER" checked={form.current_conditions.liquid_precipitation.rain_shower} onChange={(v)=>setCC('liquid_precipitation','rain_shower',v)} testId="wx-cc-rain-shower" />
                      <select value={form.current_conditions.liquid_precipitation.rain_shower_intensity} onChange={(e)=>setCC('liquid_precipitation','rain_shower_intensity',e.target.value)} className="bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-1 py-0.5">
                        {INTENSITY.map((i) => <option key={i} value={i}>{i || 'Intensity'}</option>)}
                      </select>
                    </div>
                    <CheckRow label="FREEZING RAIN" checked={form.current_conditions.liquid_precipitation.freezing_rain} onChange={(v)=>setCC('liquid_precipitation','freezing_rain',v)} testId="wx-cc-freezing-rain" />
                    <CheckRow label="MIXED PRECIP" checked={form.current_conditions.liquid_precipitation.mixed_precipitation} onChange={(v)=>setCC('liquid_precipitation','mixed_precipitation',v)} testId="wx-cc-mixed" />
                    <CheckRow label="DRIZZLE" checked={form.current_conditions.liquid_precipitation.drizzle} onChange={(v)=>setCC('liquid_precipitation','drizzle',v)} testId="wx-cc-drizzle" />
                  </div>
                  {/* Frozen */}
                  <div>
                    <div className="text-blue-300 font-semibold text-sm underline mb-1">Frozen Precipitation</div>
                    <div className="flex items-center gap-2">
                      <CheckRow label="SNOW (SN)" checked={form.current_conditions.frozen_precipitation.snow} onChange={(v)=>setCC('frozen_precipitation','snow',v)} testId="wx-cc-snow" />
                      <select value={form.current_conditions.frozen_precipitation.snow_intensity} onChange={(e)=>setCC('frozen_precipitation','snow_intensity',e.target.value)} className="bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-1 py-0.5">
                        {INTENSITY.map((i) => <option key={i} value={i}>{i || 'Intensity'}</option>)}
                      </select>
                    </div>
                    <CheckRow label="BLOWING SNOW" checked={form.current_conditions.frozen_precipitation.blowing_snow} onChange={(v)=>setCC('frozen_precipitation','blowing_snow',v)} testId="wx-cc-blowing-snow" />
                    <CheckRow label="ICE PELLETS" checked={form.current_conditions.frozen_precipitation.ice_pellets} onChange={(v)=>setCC('frozen_precipitation','ice_pellets',v)} testId="wx-cc-ice-pellets" />
                    <CheckRow label="ICE CRYSTALS" checked={form.current_conditions.frozen_precipitation.ice_crystals} onChange={(v)=>setCC('frozen_precipitation','ice_crystals',v)} testId="wx-cc-ice-crystals" />
                    <CheckRow label="SNOW GRAINS" checked={form.current_conditions.frozen_precipitation.snow_grains} onChange={(v)=>setCC('frozen_precipitation','snow_grains',v)} testId="wx-cc-snow-grains" />
                  </div>
                  {/* Intense */}
                  <div>
                    <div className="text-blue-300 font-semibold text-sm underline mb-1">Intense Phenomena</div>
                    <CheckRow label="THUNDERSTORM (TS)" checked={form.current_conditions.intense_phenomena.thunderstorm} onChange={(v)=>setCC('intense_phenomena','thunderstorm',v)} testId="wx-cc-ts" />
                    <div className="flex items-center gap-2">
                      <CheckRow label="TS IN VICINITY" checked={form.current_conditions.intense_phenomena.ts_in_vicinity} onChange={(v)=>setCC('intense_phenomena','ts_in_vicinity',v)} testId="wx-cc-ts-vicinity" />
                      <select value={form.current_conditions.intense_phenomena.ts_quadrant} onChange={(e)=>setCC('intense_phenomena','ts_quadrant',e.target.value)} className="bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded px-1 py-0.5">
                        <option value="">Quadrant</option>
                        {TS_QUADRANTS.map((q) => <option key={q} value={q}>{q}</option>)}
                      </select>
                    </div>
                    <CheckRow label="HAIL" checked={form.current_conditions.intense_phenomena.hail} onChange={(v)=>setCC('intense_phenomena','hail',v)} testId="wx-cc-hail" />
                    <CheckRow label="SAND / DUSTSTORM" checked={form.current_conditions.intense_phenomena.sand_duststorm} onChange={(v)=>setCC('intense_phenomena','sand_duststorm',v)} testId="wx-cc-sand" />
                    <CheckRow label="FUNNEL / TORNADO / WATERSPOUT" checked={form.current_conditions.intense_phenomena.funnel_cloud} onChange={(v)=>setCC('intense_phenomena','funnel_cloud',v)} testId="wx-cc-funnel" />
                  </div>
                </div>
              </div>

              {/* Measurements */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={`15. Temperature (${form.units==='imperial'?'°F':'°C'})`}><Input value={form.temperature} onChange={(e)=>setField('temperature', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-temperature" /></Field>
                <Field label="16. Humidity (%)"><Input value={form.humidity} onChange={(e)=>setField('humidity', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-humidity" /></Field>
                <Field label={`17. Dew Point (${form.units==='imperial'?'°F':'°C'})`}><Input value={form.dewPoint} onChange={(e)=>setField('dewPoint', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-dewpoint" /></Field>
                <Field label="18. Barometer (mb)"><Input value={form.barometer} onChange={(e)=>setField('barometer', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-barometer" /></Field>
                <Field label="19. Three-hour trend" className="md:col-span-2">
                  <div className="flex items-center gap-4 text-gray-100">
                    {['rising','steady','falling'].map((t) => (
                      <label key={t} className="flex items-center gap-2 capitalize">
                        <input type="radio" name="trend" checked={form.three_hour_trend===t} onChange={()=>setField('three_hour_trend', t)} data-testid={`wx-trend-${t}`} />
                        {t}
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label={`20. Wind Speed (${form.units==='imperial'?'mph':'km/h'})`}><Input value={form.windSpeed} onChange={(e)=>setField('windSpeed', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-wind-speed" /></Field>
                <Field label="21. Direction From">
                  <select value={form.wind_direction_from} onChange={(e)=>setField('wind_direction_from', e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white rounded px-2 py-2" data-testid="wx-wind-direction">
                    <option value="">—</option>
                    {CARDINAL.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label={`22. Wind Gusts (${form.units==='imperial'?'mph':'km/h'})`}><Input value={form.wind_gusts} onChange={(e)=>setField('wind_gusts', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-wind-gusts" /></Field>
                <Field label={`23. Wind Gusts Max (${form.units==='imperial'?'mph':'km/h'})`}><Input value={form.wind_gusts_max} onChange={(e)=>setField('wind_gusts_max', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-wind-gusts-max" /></Field>
                <Field label={`24. Rain 1HR (${form.units==='imperial'?'in':'mm'})`}><Input value={form.rain_1hr} onChange={(e)=>setField('rain_1hr', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-rain-1hr" /></Field>
                <Field label={`25. Rain Total (${form.units==='imperial'?'in':'mm'})`}><Input value={form.rain_total} onChange={(e)=>setField('rain_total', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-rain-total" /></Field>
                <Field label="26. Snow 1HR (cm)"><Input value={form.snow_1hr} onChange={(e)=>setField('snow_1hr', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-snow-1hr" /></Field>
                <Field label="27. Snow Total (cm)"><Input value={form.snow_total} onChange={(e)=>setField('snow_total', e.target.value)} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-snow-total" /></Field>
              </div>

              {/* Severe Weather Risk Level */}
              <div className="p-3 rounded border border-gray-700 bg-gray-800/40">
                <div className="text-gray-200 font-semibold mb-2">28. Severe Weather Risk Level</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {NWS_LEVELS.map((lvl) => {
                    const s = NWS_STYLE[lvl];
                    const active = form.nws_level === lvl;
                    return (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => setField('nws_level', lvl)}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${active ? 'ring-2 ring-white' : 'opacity-70 hover:opacity-100'}`}
                        style={{ background: s.bg, color: s.fg, borderColor: s.border }}
                        data-testid={`wx-nws-${lvl}`}
                      >
                        {lvl}
                      </button>
                    );
                  })}
                </div>
                <Input value={form.nws_details} onChange={(e)=>setField('nws_details', e.target.value)} placeholder="If multiple, use Highest Level (details)" className="bg-gray-800 border-gray-700 text-white" data-testid="wx-nws-details" />
              </div>

              <Field label="29. Notes (optional)">
                <Textarea value={form.notes} onChange={(e)=>setField('notes', e.target.value)} rows={4} className="bg-gray-800 border-gray-700 text-white" data-testid="wx-notes" />
              </Field>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-700 sticky bottom-0 bg-gray-900 rounded-b-lg">
              <Button variant="outline" onClick={closeForm} className="border-gray-600 text-gray-200 hover:bg-gray-700" data-testid="wx-form-cancel">Cancel</Button>
              <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="wx-form-save">
                {editing ? 'Update Report' : 'Save Report'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WXReportModule;
